import { useEffect, useState } from 'react'
import Image from 'next/legacy/image'
import { Tooltip } from 'react-tooltip'
import { isIOS, isChrome, isEdge, isFirefox, isOpera } from 'react-device-detect'
import cx from 'classnames'
import { InformationCircleIcon } from '@heroicons/react/outline'
import _ from 'lodash'

import useViewport from 'contexts/viewport'
import { founderBlurDataURL } from 'constants/blurDataURL'
import { parseProductName } from 'utils/utils'
import { PaidProductRarityColor } from 'lib/constants'

import styles from '../../index.module.scss'
import { InventoryItem } from '..'

interface ILootItem {
  id: number
  item: InventoryItem
  onShowCrateOpenDialog: (inventoryItem) => void
}

const LootItem: React.FunctionComponent<ILootItem> = ({ id, item, onShowCrateOpenDialog }: ILootItem) => {
  const { productName, isDayZeroPatch, isDkCrate, isDkBundle } = item

  const { isMobile } = useViewport()

  const [product, setProduct] = useState<any>({})
  const [isHovering, setIsHovering] = useState(false)
  const [imageError, setImageError] = useState(false)

  useEffect(() => {
    if (productName) {
      if (isDayZeroPatch) {
        setProduct({
          rarity: 'Legendary',
          name: 'Day Zero Claw Patch',
          category: '',
        })
      } else {
        setProduct(parseProductName(productName))
      }
    }
  }, [productName, isDayZeroPatch])

  const handleMouseEnter = () => {
    setIsHovering(true)
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
  }

  const onOpenDkCrate = () => {
    onShowCrateOpenDialog(item)
  }

  return !_.isEmpty(product) ? (
    <div className="relative">
      <div
        className="mx-auto flex h-auto w-full cursor-pointer flex-col space-y-1 hover:outline hover:outline-2 md:h-[253px]"
        style={{
          outlineColor: isHovering ? PaidProductRarityColor[product.rarity].text : 'transparent',
          boxShadow: isHovering
            ? `40px 0px 80px -60px ${PaidProductRarityColor[product.rarity].shadow}, -40px 0px 80px -60px ${
                PaidProductRarityColor[product.rarity].shadow
              }`
            : 'none',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onOpenDkCrate}
      >
        <div className={cx('h-[12px]', styles.bgStripesTilted)} />
        <div className="aspect-video h-[130px] w-full border border-white border-opacity-20 p-[6px]">
          <div
            className={cx(
              'relative flex h-[calc(130px-12px)] h-full w-full items-center justify-center p-3',
              styles.bgStripes,
            )}
          >
            {/* Item image */}
            {isDayZeroPatch ? (
              <>
                {!isIOS && (isChrome || isEdge || isFirefox || isOpera) ? (
                  <video autoPlay muted loop playsInline className="h-full w-full object-contain object-center">
                    <source src="/images/profile/clawnft_animation10.webm" type="video/webm" />
                  </video>
                ) : (
                  <Image
                    src="/images/profile/clawnft_animation10.gif"
                    layout="fill"
                    objectFit="cover"
                    alt="Claws NFT Animation"
                  />
                )}
              </>
            ) : (
              <Image
                src={imageError ? '/images/logo-yellow.png' : `/images/profile/paid-products/${productName}.png`}
                layout="fill"
                objectFit={isDkCrate ? 'cover' : 'contain'}
                alt={productName}
                placeholder="blur"
                blurDataURL={founderBlurDataURL}
                onError={(event) => setImageError(true)}
                onEmptied={() => setImageError(true)}
              />
            )}
          </div>
        </div>
        <div
          className="flex h-[calc(253px-12px-130px-8px)] flex-col items-center justify-center space-y-2 border border-white border-opacity-20"
          style={{
            background: `linear-gradient(180deg, rgba(28, 28, 28, 0.5) 0%, transparent 100%), radial-gradient(58.54% 58.54% at 50% 117.07%, ${
              PaidProductRarityColor[product.rarity].bg
            }  0%, transparent 100%), linear-gradient(0deg, transparent, transparent)`,
          }}
        >
          <span className="text-center font-tactic-extended text-base font-bold leading-[18px] tracking-[0.015em]">
            {product.name}
          </span>
          <span className="text-center font-tactic-extended text-base font-normal leading-[14px] tracking-[0em] text-[#8C8C8C]">
            {product.category || <br />}
          </span>
          <span
            className="text-center font-tactic-extended text-base font-normal leading-[14px] tracking-[0em]"
            style={{
              color: PaidProductRarityColor[product.rarity].text,
            }}
          >
            {product.rarity.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Tool button */}
      <div id={'moreInfo_' + id} className="absolute left-0 top-0 z-10 cursor-pointer pb-1 pl-3 pr-1 pt-8">
        <InformationCircleIcon width={isMobile ? 40 : 24} height={isMobile ? 40 : 24} />
        <Tooltip
          anchorSelect={'#moreInfo_' + id}
          place="bottom"
          className={styles.tooltipInfo}
          classNameArrow={styles.toolArrow}
        >
          {!isDkCrate && (
            <div>
              <span className="font-tactic text-[12px] font-semibold uppercase leading-4 !text-white antialiased">
                Special Item
              </span>
              {!isDayZeroPatch && (
                <>
                  <hr className="mb-2 border-[#D7D7D7]" />
                  <span className="font-tactic text-[11px] font-light leading-4 !text-white antialiased">
                    {isDkBundle
                      ? 'This item is temporarily unavailable in DEADROP but will be available with Snapshot VII'
                      : 'This Item is temporarily unavailable in DEADROP'}
                  </span>
                </>
              )}
            </div>
          )}
          {!isDayZeroPatch && (
            <div className={isDkCrate ? '' : 'mt-2'}>
              <span className="font-tactic text-[13px] font-semibold uppercase leading-4 !text-white antialiased">
                Item lock
              </span>
              <hr className="mb-2 border-[#D7D7D7]" />
              <span className="font-tactic text-[11px] font-light leading-4 !text-white antialiased">
                {isDkCrate || isDkBundle
                  ? 'This item is currently locked from being transferred. Transfer capability to friends will be enabled in the future'
                  : 'This item is not transferable'}
              </span>
            </div>
          )}
        </Tooltip>
      </div>
    </div>
  ) : (
    <></>
  )
}

export default LootItem
